/**
 * Score ranking in a rule (user, 12/10/2026: "why we dont have the score
 * ranking? isnt it the raw score?"): the Flow Optimizer's condition — Raw
 * score is the highest / the lowest / in the top or bottom N% — judged on the
 * item's rank among the items its formula ranked, with the Flow Optimizer's
 * own check (`checkRanking`: a pool of one names no best or worst). An item
 * the formula did not rank (excluded, no data) is not judged; when every
 * ranked score is 0 there is nothing to rank. Exclude rules decide WHO is
 * ranked, so they cannot read the ranking: refused on write, read as false.
 * For Media Devoted | Created By Ciro Autiero
 */
import { checkRanking } from "../../../flow-tag-rules/ranking";
import type { DropGenRankingOp, DropGenRuleNode } from "../rules.types";

export const DROP_GEN_RANKING_OPS: readonly DropGenRankingOp[] = ["highest", "lowest", "top_percent", "bottom_percent"];

/** An item's place among the ranked items of its surface. */
export interface ScoreRank {
  /** 1-based; null = not ranked (excluded, no data). */
  rank: number | null;
  /** How many items are ranked. */
  size: number;
  score: number | null;
  /** False when every ranked score is 0 — no signal to rank on. */
  signal: boolean;
}

type Ranked = { rank: number | null; score: number | null };

/** The rank context of every item of one ranking (its ranks are already set, best first). */
export function rankPool(items: ReadonlyArray<Ranked>): (item: Ranked) => ScoreRank {
  const ranked = items.filter((i) => i.rank !== null);
  const signal = ranked.some((i) => (i.score ?? 0) !== 0);
  return (item) => ({ rank: item.rank, size: ranked.length, score: item.score, signal });
}

type RankingNode = Extract<DropGenRuleNode, { kind: "ranking" }>;

/** TRUE / FALSE on a ranked item; UNKNOWN on one not ranked; FALSE with no ranking at all (Exclude). */
export function evaluateRanking(node: RankingNode, ctx: ScoreRank | undefined): boolean | null {
  if (!ctx) return false;
  if (ctx.rank === null || !ctx.signal) return null;
  return checkRanking({ rank: ctx.rank, size: ctx.size }, node.op, node.percent);
}

const fmt = (v: number) => (Number.isInteger(v) ? v.toLocaleString("en-US") : v.toFixed(2));

/** "Raw score 4.12 is the highest (#1 of 24)" / "Raw score 0.31 in the bottom 20% (#22 of 24)". */
export function describeRanking(node: RankingNode, ctx: ScoreRank | undefined): string {
  const what =
    node.op === "highest" || node.op === "lowest"
      ? `is the ${node.op}`
      : `in the ${node.op === "top_percent" ? "top" : "bottom"} ${node.percent ?? 0}%`;
  if (!ctx || ctx.rank === null) return `Raw score ${what} — not ranked`;
  return `Raw score ${ctx.score === null ? "—" : fmt(ctx.score)} ${what} (#${ctx.rank} of ${ctx.size})`;
}

/** Does this tree read the ranking anywhere? */
export function usesRanking(node: DropGenRuleNode): boolean {
  if (node.kind === "ranking") return true;
  if (node.kind === "not") return usesRanking(node.child);
  return node.kind === "group" && node.children.some(usesRanking);
}

/** Problems of a ranking node, in words. */
export function rankingNodeErrors(r: Record<string, unknown>, path: string): string[] {
  const errs: string[] = [];
  if (r.metric !== "rawScore") errs.push(`${path}: a score ranking reads the raw score`);
  if (!DROP_GEN_RANKING_OPS.includes(r.op as DropGenRankingOp)) errs.push(`${path}: highest, lowest, top % or bottom %`);
  const needsPercent = r.op === "top_percent" || r.op === "bottom_percent";
  const p = r.percent;
  if (needsPercent && !(typeof p === "number" && Number.isInteger(p) && p >= 1 && p <= 100)) errs.push(`${path}: a percent from 1 to 100`);
  return errs;
}
